import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const LANGUAGE_CODES = [
  { value: 'en', short: 'EN' },
  { value: 'uk', short: 'UK' },
  { value: 'ru', short: 'RU' },
];

export function LanguageToggle({ isDark = false, activeCode, onChange, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const active =
    LANGUAGE_CODES.find(({ value }) => value === activeCode) || LANGUAGE_CODES[0];

  useEffect(() => {
    const onDoc = e => {
      if (open && rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleSelect = value => {
    onChange(value);
    setOpen(false);
  };

  const triggerClass = `inline-flex h-10 items-center gap-1.5 rounded-full border pl-1 pr-2 transition ${
    isDark
      ? 'border-slate-700 bg-slate-800/90 hover:bg-slate-700/90'
      : 'border-orange-100 bg-white shadow-sm hover:bg-orange-50/60'
  }`;

  const menuClass = `absolute right-0 top-full z-[320] mt-2 min-w-[7rem] overflow-hidden rounded-2xl border py-1.5 shadow-lg ${
    isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'
  }`;

  const itemClass = isActive =>
    `flex w-full items-center justify-center px-3 py-2 transition ${
      isActive
        ? isDark
          ? 'bg-slate-800 text-[#FF7601]'
          : 'bg-orange-50 text-[#FF7601]'
        : isDark
        ? 'text-slate-200 hover:bg-slate-800/90'
        : 'text-slate-700 hover:bg-orange-50/80'
    }`;

  const codeCircleClass = isActive =>
    `inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold uppercase tracking-wide ${
      isActive
        ? 'bg-[#FF7601] text-white shadow-[0_2px_8px_rgba(255,118,1,0.35)]'
        : isDark
        ? 'border border-slate-600 text-slate-400'
        : 'border border-orange-100 text-slate-500'
    }`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerClass}
      >
        <span className={codeCircleClass(true)}>{active.short}</span>
        <ChevronDown
          size={16}
          className={`shrink-0 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          } ${isDark ? 'text-orange-400' : 'text-[#FF7601]'}`}
          aria-hidden
        />
      </button>

      {open && (
        <ul role="listbox" aria-label={ariaLabel} className={menuClass}>
          {LANGUAGE_CODES.map(({ value, short }) => {
            const isActive = activeCode === value;

            return (
              <li key={value} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => handleSelect(value)}
                  className={itemClass(isActive)}
                >
                  <span className={codeCircleClass(isActive)}>{short}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
